import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Input,
  Button,
  SimpleGrid } from
'@chakra-ui/react';
import { ShieldAlert, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
export function Hero() {
  const [url, setUrl] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isScanning) {
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            navigate('/report/c01e7c1f30f041c6bbfd34ccb1d993ad?purchased=true');
            return 100;
          }
          return prev + 2;
        });
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isScanning, navigate]);
  const handleScan = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) return;
    setIsScanning(true);
  };
  return (
    <Box
      as="section"
      position="relative"
      pt={{
        base: 14,
        lg: 20
      }}
      pb={{
        base: 12,
        lg: 16
      }}
      overflow="hidden">
      
      {/* Background glow */}
      <Box
        position="absolute"
        top={0}
        left="50%"
        transform="translateX(-50%)"
        w="800px"
        h="400px"
        bg="rgba(16, 185, 129, 0.15)"
        filter="blur(120px)"
        borderRadius="full"
        pointerEvents="none" />
      

      <Container maxW="container.xl" px={4} position="relative" zIndex={10}>
        <Box maxW="3xl" mx="auto" textAlign="center">
          <MotionBox
            initial={{
              opacity: 0,
              y: 20
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.5
            }}
            display="inline-flex"
            alignItems="center"
            gap={2}
            px={3}
            py={1}
            borderRadius="full"
            bg="rgba(16, 185, 129, 0.1)"
            border="1px solid"
            borderColor="rgba(16, 185, 129, 0.2)"
            color="brand.green"
            fontSize="sm"
            fontWeight="medium"
            mb={5}>
            
            <ShieldAlert size={16} />
            <Text as="span">Google Merchant Center Reinstatement Tool</Text>
          </MotionBox>

          <MotionBox
            initial={{
              opacity: 0,
              y: 20
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.5,
              delay: 0.1
            }}>
            
            <Heading
              as="h1"
              fontSize={{
                base: '3xl',
                md: '4xl',
                lg: '5xl'
              }}
              fontWeight="bold"
              letterSpacing="tight"
              mb={3}
              lineHeight="1.1">
              
              Rejected by Google Merchant Center?
            </Heading>
          </MotionBox>

          <MotionBox
            initial={{
              opacity: 0,
              y: 20
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.5,
              delay: 0.2
            }}>
            
            <Text
              fontSize={{
                base: 'md',
                md: 'lg'
              }}
              color="gray.400"
              mb={8}
              maxW="xl"
              mx="auto">
              
              Scan your store for the reinstatement blockers Google won't tell
              you about.
            </Text>
          </MotionBox>

          <MotionBox
            initial={{
              opacity: 0,
              y: 20
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.5,
              delay: 0.3
            }}
            maxW="xl"
            mx="auto">
            
            {/* Input Card */}
            <Box
              bg="brand.card"
              border="1px solid"
              borderColor="brand.border"
              borderRadius="xl"
              p={5}
              boxShadow="glow">
              
              <form
                onSubmit={handleScan}
                style={{
                  width: '100%'
                }}>
                
                <Box position="relative" w="full">
                  <Input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    isDisabled={isScanning}
                    placeholder="https://yourstore.com"
                    bg="brand.dark"
                    border="1px solid"
                    borderColor="brand.border"
                    borderRadius="lg"
                    color="white"
                    fontSize="sm"
                    py="3.5"
                    px={4}
                    _placeholder={{
                      color: 'gray.500'
                    }}
                    _focus={{
                      ring: 2,
                      ringColor: 'brand.green',
                      borderColor: 'transparent'
                    }}
                    _disabled={{
                      opacity: 0.5
                    }} />
                  
                  {!isScanning &&
                  <Box
                    position="absolute"
                    right={2}
                    top="50%"
                    transform="translateY(-50%)">
                    
                      <Box
                      w={3}
                      h={3}
                      borderRadius="full"
                      bg="rgba(16, 185, 129, 0.6)"
                      className="animate-pulse" />
                    
                    </Box>
                  }
                </Box>

                <Text
                  fontSize="xs"
                  color="gray.500"
                  mt={2}
                  mb={3}
                  textAlign="left">
                  
                  This does not submit an appeal or change your Google account.
                </Text>

                {isScanning ?
                <Box w="full">
                    <Box
                    w="full"
                    bg="brand.dark"
                    border="1px solid"
                    borderColor="brand.border"
                    borderRadius="lg"
                    h="40px"
                    overflow="hidden"
                    position="relative">
                    
                      <Box
                      bg="brand.green"
                      h="full"
                      borderRadius="lg"
                      transition="all 0.1s linear"
                      w={`${progress}%`} />
                    
                      <Flex
                      position="absolute"
                      inset={0}
                      align="center"
                      justify="center"
                      fontSize="sm"
                      fontWeight="semibold"
                      color="white">
                      
                        {progress}%
                      </Flex>
                    </Box>
                  </Box> :

                <Button
                  type="submit"
                  isDisabled={!url}
                  w="full"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                  px={6}
                  py={3}
                  fontSize="md"
                  fontWeight="semibold"
                  color="white"
                  bg="brand.green"
                  borderRadius="lg"
                  _hover={{
                    bg: 'brand.greenHover',
                    boxShadow: 'glow-strong'
                  }}
                  _disabled={{
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    _hover: {
                      boxShadow: 'none'
                    }
                  }}
                  h="auto">
                  
                    <Text as="span">Scan My Store</Text>
                    <ArrowRight size={18} />
                  </Button>
                }
              </form>
            </Box>
          </MotionBox>

          {/* Stat Cards */}
          <MotionBox
            initial={{
              opacity: 0,
              y: 20
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.5,
              delay: 0.45
            }}>
            
            <SimpleGrid
              columns={{
                base: 2,
                md: 4
              }}
              spacing={3}
              mt={8}
              maxW="2xl"
              mx="auto">
              
              <Box
                bg="brand.card"
                border="1px solid"
                borderColor="brand.border"
                borderRadius="lg"
                px={4}
                py={3}
                textAlign="center">
                
                <Text fontSize="xl" fontWeight="bold" color="white">
                  ~60s
                </Text>
                <Text fontSize="xs" color="gray.400" mt={0.5}>
                  scan time
                </Text>
              </Box>
              <Box
                bg="brand.card"
                border="1px solid"
                borderColor="brand.border"
                borderRadius="lg"
                px={4}
                py={3}
                textAlign="center">
                
                <Text fontSize="xl" fontWeight="bold" color="white">
                  200+
                </Text>
                <Text fontSize="xs" color="gray.400" mt={0.5}>
                  hidden checks
                </Text>
              </Box>
              <Box
                bg="brand.card"
                border="1px solid"
                borderColor="brand.border"
                borderRadius="lg"
                px={4}
                py={3}
                textAlign="center">
                
                <Text fontSize="xl" fontWeight="bold" color="red.400">
                  Prevent
                </Text>
                <Text fontSize="xs" color="gray.400" mt={0.5}>
                  repeat rejections
                </Text>
              </Box>
              <Box
                bg="brand.card"
                border="1px solid"
                borderColor="brand.border"
                borderRadius="lg"
                px={4}
                py={3}
                textAlign="center">
                
                <Text fontSize="xl" fontWeight="bold" color="brand.green">
                  Re-scan
                </Text>
                <Text fontSize="xs" color="gray.400" mt={0.5}>
                  included after fixes
                </Text>
              </Box>
            </SimpleGrid>
          </MotionBox>
        </Box>
      </Container>
    </Box>);

}
